import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from "@/components/ui/select";
import { RISK_COLORS, RISK_LABELS } from "@/lib/agents";
import { ShieldCheck, ShieldOff, Play } from "lucide-react";
import { toast } from "sonner";

const RISK_STYLES = {
  low: "#10B981", medium: "#EAB308", high: "#F97316", critical: "#EF4444",
};

export default function Tools() {
  const [tools, setTools] = useState([]);
  const [masters, setMasters] = useState([]);
  const [selectedAgent, setSelectedAgent] = useState("");
  const [selectedTool, setSelectedTool] = useState("");
  const [decision, setDecision] = useState(null);

  useEffect(() => {
    api.get("/tools").then((r) => setTools(r.data.tools));
    api.get("/agents").then((r) => setMasters(r.data.agents.filter((a) => a.status === "active")));
  }, []);

  const evaluate = async () => {
    if (!selectedAgent || !selectedTool) return toast.error("Elige agente y herramienta");
    try {
      const { data } = await api.post("/gatekeeper/evaluate", {
        agent_id: selectedAgent, tool_id: selectedTool,
      });
      setDecision(data);
      toast.success(`Gatekeeper: ${data.allow ? "PERMITE" : "BLOQUEA"}`);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Fallo");
    }
  };

  const categories = [...new Set(tools.map((t) => t.category))];

  return (
    <div className="p-8 space-y-6" data-testid="tools-page">
      <div className="border-b border-[var(--border)] pb-5">
        <div className="label-mono mb-2">CATÁLOGO · TOOL / ACTION LAYER</div>
        <h1 className="font-display font-black text-4xl tracking-tight">Herramientas</h1>
        <p className="text-sm text-[var(--text-dim)] mt-1">
          Cada herramienta tiene un nivel de riesgo. El Security Gatekeeper decide si un agente puede usarla y con qué gate.
        </p>
      </div>

      {/* Gatekeeper simulator */}
      <div className="border border-[var(--accent)] bg-[var(--surface)] p-5" data-testid="gatekeeper-panel">
        <div className="flex items-center gap-2 mb-3">
          <ShieldCheck className="w-4 h-4 text-[var(--accent)]" />
          <div className="label-mono">SECURITY GATEKEEPER — SIMULADOR</div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
          <div>
            <Label className="label-mono">Agente</Label>
            <Select value={selectedAgent} onValueChange={setSelectedAgent}>
              <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="gk-agent">
                <SelectValue placeholder="Elige agente" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
                {masters.map((m) => <SelectItem key={m.id} value={m.id}>{m.name}</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="label-mono">Herramienta</Label>
            <Select value={selectedTool} onValueChange={setSelectedTool}>
              <SelectTrigger className="rounded-none bg-[var(--bg)] border-[var(--border)] font-mono" data-testid="gk-tool">
                <SelectValue placeholder="Elige tool" />
              </SelectTrigger>
              <SelectContent className="rounded-none bg-[var(--surface)] border-[var(--border)]">
                {tools.map((t) => <SelectItem key={t.id} value={t.id}>{t.name} ({t.risk})</SelectItem>)}
              </SelectContent>
            </Select>
          </div>
          <div className="flex items-end">
            <Button onClick={evaluate} className="w-full rounded-none bg-[var(--accent)] text-[var(--bg)] hover:bg-[var(--accent)]/80" data-testid="gk-eval">
              <Play className="w-4 h-4 mr-2" /> Evaluar
            </Button>
          </div>
        </div>
        {decision && (
          <div className="mt-4 p-4 border border-[var(--border)] bg-[var(--bg)]" data-testid="gk-decision">
            <div className="grid grid-cols-3 gap-4">
              <div>
                <div className="label-mono mb-1">DECISIÓN</div>
                <div className={`font-display font-black text-xl ${decision.allow ? "text-[var(--good)]" : "text-[var(--danger)]"}`}>
                  {decision.allow ? "PERMITIDO" : "BLOQUEADO"}
                </div>
              </div>
              <div>
                <div className="label-mono mb-1">APROBACIÓN HUMANA</div>
                <div className={`font-display font-bold text-xl ${decision.requires_approval ? "text-[var(--warn)]" : "text-[var(--text-dim)]"}`}>
                  {decision.requires_approval ? "SÍ" : "NO"}
                </div>
              </div>
              <div>
                <div className="label-mono mb-1">AUTONOMÍA AGENTE</div>
                <div className="font-display font-bold text-xl text-white">L{decision.autonomy_level ?? "?"}</div>
              </div>
            </div>
            <div className="mt-3 pt-3 border-t border-[var(--border)] text-sm text-[var(--text-dim)]">
              {decision.reason}
            </div>
          </div>
        )}
      </div>

      {/* Tools list grouped by category */}
      {categories.map((cat) => (
        <div key={cat}>
          <div className="label-mono mb-3">{cat.toUpperCase()}</div>
          <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-3">
            {tools.filter((t) => t.category === cat).map((t) => (
              <div key={t.id} className="border border-[var(--border)] bg-[var(--surface)] p-4" data-testid={`tool-${t.id}`}>
                <div className="flex items-start justify-between mb-2">
                  <div className="font-display font-bold text-base">{t.name}</div>
                  <Badge className="rounded-none font-mono text-[10px]" style={{ background: RISK_STYLES[t.risk], color: "#0B0E14" }}>
                    {t.risk}
                  </Badge>
                </div>
                <div className="font-mono text-[10px] text-[var(--text-dim)] mb-2">{t.id}</div>
                <div className="flex items-center gap-2 text-xs">
                  {t.requires_approval ? (
                    <><ShieldCheck className="w-3 h-3 text-[var(--warn)]" /><span className="text-[var(--warn)]">Requiere aprobación</span></>
                  ) : (
                    <><ShieldOff className="w-3 h-3 text-[var(--text-dim)]" /><span className="text-[var(--text-dim)]">Auto-ejecutable</span></>
                  )}
                </div>
              </div>
            ))}
          </div>
        </div>
      ))}
    </div>
  );
}
